import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { CreditCard, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Launches the GoodLeap mobile app via the org's deep-link scheme,
 * falling back to the partner URL after a short delay.
 */
export function GoodLeapButton() {
  const { orgId } = useAuth();
  const [partnerUrl, setPartnerUrl] = useState<string | null>(null);
  const [appScheme, setAppScheme] = useState<string>("goodleap://");

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const { data } = await supabase
        .from("organizations")
        .select("goodleap_partner_url, goodleap_app_scheme")
        .eq("id", orgId)
        .single();
      if (data) {
        setPartnerUrl(data.goodleap_partner_url || null);
        setAppScheme(data.goodleap_app_scheme || "goodleap://");
      }
    })();
  }, [orgId]);

  if (!partnerUrl) return null;

  const launch = () => {
    const start = Date.now();
    // Try app deep link
    window.location.href = appScheme;
    // Fall back to web URL after short delay if still here
    setTimeout(() => {
      if (Date.now() - start < 1600) {
        window.open(partnerUrl, "_blank", "noopener,noreferrer");
        toast.info("Opening GoodLeap web — install the app for one-tap access");
      }
    }, 800);
  };

  return (
    <Button variant="outline" size="sm" className="gap-2" onClick={launch}>
      <CreditCard className="w-4 h-4" />
      Run GoodLeap Financing
      <ExternalLink className="w-3 h-3 opacity-60" />
    </Button>
  );
}
